<div class="bg-white rounded-xl border border-blueGray-100 shadow-sm p-6 w-full max-w-4xl mx-auto animate-in fade-in duration-200">
  <!-- Top Bar -->
  <div class="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-blueGray-100 pb-4 mb-6 gap-4">
    <div class="flex items-center gap-3">
      <button (click)="onClose.emit()" class="text-blueGray-400 hover:text-blueGray-600 focus:outline-none transition-colors">
        <i class="fa fa-arrow-left text-lg"></i>
      </button>
      <div>
        <span class="text-xs font-semibold uppercase tracking-wider text-blueGray-400">Expense Report</span>
        <h2 class="text-xl font-bold text-blueGray-800">{{ report ? 'Edit Draft Report' : 'Create New Report' }}</h2>
      </div>
    </div>

    <!-- Summary Statistics Badge -->
    <div class="flex items-center gap-4 bg-blueGray-50 rounded-xl p-3 border border-blueGray-100">
      <div class="text-left">
        <span class="block text-[10px] font-bold uppercase text-blueGray-400 tracking-wider">Total Expenses</span>
        <span class="text-lg font-extrabold text-primary-500 flex items-baseline">
          <span class="text-xs font-normal text-blueGray-400 uppercase mr-1">{{ (groupCurrency || 'USD') | uppercase }}</span>
          <span>{{ totalAmount | number: '1.2-2' }}</span>
        </span>
      </div>
      <div class="h-8 w-[1px] bg-blueGray-200"></div>
      <div>
        <span class="block text-[10px] font-bold uppercase text-blueGray-400 tracking-wider">Status</span>
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
          <i class="fa fa-file-signature text-[10px] mr-1"></i> Draft
        </span>
      </div>
    </div>
  </div>

  <!-- Title Form -->
  <div class="mb-6">
    <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-2">Report Title / Purpose *</label>
    <input type="text" [(ngModel)]="title" placeholder="e.g. Travel Expenses June, Office Party Supplies..."
      class="w-full px-4 py-2.5 border border-blueGray-200 rounded-lg text-sm bg-blueGray-50 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none transition-all" />
  </div>

  <!-- Expenses List Section -->
  <div class="mb-8">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-md font-bold text-blueGray-800 flex items-center gap-2">
        <i class="fa fa-receipt text-primary-500"></i> Expenses list
      </h3>
      <button (click)="openAddExpenseModal()"
        class="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-600 bg-white hover:bg-indigo-50 shadow-sm font-semibold py-1.5 px-3 rounded-lg text-xs transition-all focus:outline-none">
        <i class="fa fa-plus"></i> Add Item
      </button>
    </div>

    <!-- Expenses Table -->
    <div class="border border-blueGray-100 rounded-xl overflow-hidden bg-blueGray-50">
      @if (expenses.length === 0) {
      <div class="flex flex-col items-center justify-center py-12 text-blueGray-400">
        <i class="fa fa-receipt text-3xl mb-2"></i>
        <p class="text-xs">No expenses added to this report yet.</p>
        <button (click)="openAddExpenseModal()" class="text-xs font-semibold text-primary-500 hover:text-primary-600 mt-2">
          Click here to add one
        </button>
      </div>
      } @else {
      <table class="w-full text-left border-collapse bg-white">
        <thead>
          <tr class="border-b border-blueGray-100 text-[10px] font-bold uppercase text-blueGray-400 bg-blueGray-50">
            <th class="py-2.5 px-3">Date</th>
            <th class="py-2.5 px-3">Description</th>
            <th class="py-2.5 px-3">Payer & Payee</th>
            <th class="py-2.5 px-3 text-right">Amount</th>
            <th class="py-2.5 px-3 text-center">Receipt</th>
            <th class="py-2.5 px-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (item of expenses; track $index) {
          <tr class="border-b border-blueGray-50 hover:bg-blueGray-50 transition-colors text-sm text-blueGray-600">
            <td class="py-2.5 px-3 whitespace-nowrap">{{ item.date }}</td>
            <td class="py-2.5 px-3 font-medium text-blueGray-800">{{ item.description }}</td>
            <td class="py-2.5 px-3 text-xs">
              <span class="block">From: {{ item.from || 'Group' }}</span>
              <span class="block text-primary-500">To: {{ item.to }}</span>
            </td>
            <td class="py-2.5 px-3 text-right font-semibold text-blueGray-800">
              <div class="flex items-baseline justify-end">
                <span class="text-[10px] font-normal text-blueGray-400 uppercase mr-1">{{ (item.currency || 'USD') | uppercase }}</span>
                <span>{{ item.amount | number: '1.2-2' }}</span>
              </div>
              @if (item.currency && item.currency.toLowerCase() !== (groupCurrency || 'USD').toLowerCase()) {
              <div class="text-[10px] text-blueGray-400 font-normal flex items-baseline justify-end mt-0.5">
                <span>(</span>
                <span class="text-[9px] font-normal uppercase mr-0.5">{{ (groupCurrency || 'USD') | uppercase }}</span>
                <span>{{ (item.amount * (item.conversionRate || 1)) | number: '1.2-2' }} equivalent)</span>
              </div>
              }
            </td>
            <td class="py-2.5 px-3 text-center">
              @if (item.receiptUrl) {
              <a [href]="item.receiptUrl" target="_blank" class="text-primary-500 hover:text-primary-600 focus:outline-none">
                <i class="fa fa-paperclip text-sm"></i> View
              </a>
              } @else {
              <span class="text-xs text-blueGray-300">None</span>
              }
            </td>
            <td class="py-2.5 px-3 text-center">
              <div class="flex items-center justify-center gap-2">
                <button (click)="openEditExpenseModal($index)" class="text-blueGray-400 hover:text-primary-500 transition-colors">
                  <i class="fa fa-pen text-xs"></i>
                </button>
                <button (click)="deleteExpense($index)" class="text-blueGray-400 hover:text-red-600 transition-colors">
                  <i class="fa fa-trash text-xs"></i>
                </button>
              </div>
            </td>
          </tr>
          }
        </tbody>
      </table>
      }
    </div>
  </div>

  <!-- Bottom Save Bar -->
  <div class="flex items-center justify-end gap-3 border-t border-blueGray-100 pt-6">
    <button (click)="onClose.emit()" [disabled]="isSavingReport"
      class="border border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50 font-semibold py-2 px-4 rounded-lg text-sm transition-all focus:outline-none">
      Cancel
    </button>
    <button (click)="saveDraft()" [disabled]="isSavingReport || isUploadingFile"
      class="bg-blueGray-600 hover:bg-blueGray-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-all focus:outline-none shadow-sm flex items-center gap-1.5">
      <i class="fa" [ngClass]="isSavingReport ? 'fa-spinner animate-spin': 'fa-save'"></i>
      Save Draft
    </button>
    @if (isTreasurer) {
    <button (click)="publishReport()" [disabled]="isSavingReport || isUploadingFile || expenses.length === 0"
      class="bg-secondary-500 hover:bg-secondary-600 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-all focus:outline-none shadow-sm flex items-center gap-1.5">
      <i class="fa" [ngClass]="isSavingReport ? 'fa-spinner animate-spin': 'fa-paper-plane'"></i>
      Publish to Ledger
    </button>
    }
  </div>
</div>

<!-- Modal Pop-Up for Adding/Editing individual expense -->
@if (showExpenseModal) {
<div class="fixed inset-0 bg-blueGray-900 bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
  <div class="bg-white rounded-2xl max-w-lg w-full shadow-2xl p-6 border border-blueGray-100 relative animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
    
    <!-- Modal Close -->
    <button (click)="showExpenseModal = false" [disabled]="isUploadingFile || isScanningReceipt"
      class="absolute right-4 top-4 text-blueGray-400 hover:text-blueGray-600 focus:outline-none">
      <i class="fa fa-times text-lg"></i>
    </button>

    <h3 class="text-lg font-bold text-blueGray-800 mb-4">
      {{ editingExpenseIndex !== null ? 'Modify Expense Item' : 'Add Expense Item' }}
    </h3>

    <!-- Receipt Scan / Upload block -->
    <div class="bg-blueGray-50 border border-dashed border-blueGray-200 rounded-xl p-4 mb-4 text-center">
      <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-2">Receipt Document (Image or PDF)</label>
      <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
        <input type="file" (change)="onFileSelected($event)" accept="image/*,application/pdf" id="modalFile" class="hidden" [disabled]="isScanningReceipt" />
        <label for="modalFile" class="cursor-pointer flex items-center gap-1 bg-white hover:bg-blueGray-50 text-blueGray-700 border border-blueGray-200 font-semibold py-1.5 px-3 rounded-lg text-xs transition-all shadow-none">
          <i class="fa fa-upload text-[10px]"></i>
          {{ selectedFile ? 'Change Receipt' : 'Upload Receipt' }}
        </label>
        @if (selectedFile) {
        <button type="button" (click)="scanReceipt()" [disabled]="isScanningReceipt"
          class="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-600 bg-white hover:bg-indigo-50 shadow-sm font-semibold py-1.5 px-3 rounded-lg text-xs transition-all focus:outline-none">
          <i class="fa" [ngClass]="isScanningReceipt ? 'fa-spinner animate-spin': 'fa-robot'"></i>
          Scan with AI
        </button>
        }
      </div>
      @if (selectedFile) {
      <span class="block text-xs text-blueGray-400 mt-2 font-mono truncate max-w-full">
        Selected: {{ selectedFile.name }} ({{ selectedFile.size | number }} bytes)
      </span>
      }
    </div>

    <!-- Manual Expense Form -->
    <form [formGroup]="expenseForm" (ngSubmit)="addExpenseToReport()" class="flex flex-col gap-4">
      
      <!-- Currency & Amount -->
      <div class="flex gap-2">
        <div class="w-1/3">
          <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Currency *</label>
          <select formControlName="currency"
            class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none">
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
            <option value="gbp">GBP</option>
          </select>
        </div>
        <div class="w-2/3">
          <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Amount *</label>
          <input type="number" step="0.01" formControlName="amount" placeholder="0.00"
            class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>
      </div>

      <!-- Conversion Rate -->
      @if (expenseForm.get('currency')?.value !== (groupCurrency || 'USD').toLowerCase()) {
      <div class="animate-in fade-in slide-in-from-top-1 duration-200">
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Conversion Rate *</label>
        <div class="flex gap-2 items-center">
          <span class="text-sm text-blueGray-500">1 {{ expenseForm.get('currency')?.value | uppercase }} =</span>
          <input type="number" step="0.00001" formControlName="conversionRate" placeholder="1.0"
            class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
          <span class="text-sm text-blueGray-500">{{ (groupCurrency || 'USD') | uppercase }}</span>
        </div>
        <p class="text-xs text-blueGray-400 mt-1 font-normal normal-case leading-relaxed">
          Specify the conversion rate to calculate the equivalent amount in the group's default currency.
        </p>
      </div>
      }

      <!-- Description -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Description *</label>
        <input type="text" formControlName="description" placeholder="e.g. Flight ticket to conference"
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
      </div>

      <!-- From / Payer -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Paid From (Payer Name/Source)</label>
        <input type="text" formControlName="from" placeholder="e.g. Group Account, Treasurer, Member Name"
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
      </div>

      <!-- To / Paid To -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Paid To *</label>
        <input type="text" formControlName="to" placeholder="e.g. Airline, Supermarket, Consultant Name"
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
      </div>

      <!-- Date -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Date *</label>
        <input type="date" formControlName="date"
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
      </div>

      <!-- Tags -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Tags (comma-separated)</label>
        <input type="text" formControlName="tags" placeholder="e.g. travel, conference, supplies"
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-sm text-blueGray-700 focus:border-primary-500 focus:outline-none" />
      </div>

      <!-- Footer Buttons -->
      <div class="flex items-center justify-end gap-2 border-t border-blueGray-100 pt-4 mt-2">
        <button type="button" (click)="showExpenseModal = false" [disabled]="isUploadingFile || isScanningReceipt"
          class="border border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50 font-semibold py-2 px-4 rounded-lg text-sm transition-all focus:outline-none">
          Cancel
        </button>
        <button type="submit" [disabled]="(expenseForm.invalid && !isScanningReceipt) || isUploadingFile || isScanningReceipt"
          class="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-all focus:outline-none shadow-sm flex items-center gap-1.5">
          <i class="fa" [ngClass]="(isUploadingFile || isScanningReceipt) ? 'fa-spinner animate-spin': 'fa-check'"></i>
          {{ editingExpenseIndex !== null ? 'Apply Changes' : 'Add to Report' }}
        </button>
      </div>
    </form>
  </div>
</div>
}
